import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { compose, withStateHandlers, branch, renderComponent } from 'recompose';
import withCookies from 'react-cookie/lib/withCookies';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { get } from 'lodash';

import { scrollToElement } from '@sparefoot/react-core/utils/scrolling';
import { mapDispatchToActions } from '@sparefoot/react-core/utils';

import { metaPropTypes, phonePropType } from 'config/propTypes';
import {
	withSearchHandler,
	withErrorHandler,
	withViewportWidth,
} from 'decorators';
import { isLarge } from 'utils/breakpoints';
import { withSearchPageHandlers } from 'decorators/search';

import { H2 } from 'components/core/Typography';
import { LoadingPage } from 'components/core/LoadingPage';
import { LazyHydrate } from 'components/core/LazyHydrate';
import { Page } from 'components/core/Page';
import { SearchContent } from 'components/search/SearchContent';
import { SearchResults } from 'components/search/SearchResults';
import { PaginationSummary } from 'components/search/PaginationSummary';
import { SearchPagination } from 'components/search/SearchPagination';
// Legacy SearchToolBar replaced locally on NewSearchPage with redesigned NewSearchToolbar (normalized casing)
import NewSearchToolbar from 'components/search/NewSearchToolbar';
import SearchFiltersTray from 'components/search/SearchFiltersTray';
import SearchPageFooter from 'components/search/SearchPageFooter';
import { SearchMap } from 'components/search/SearchMap';

import searchSelector from 'store/selectors/search';
import {
	getSearchResults,
	getSearchLandingPage,
	showSearchFilters,
	hideSearchFilters,
	updateSearchFilters,
} from 'store/modules/search';
import { withPageViewTracking } from 'components/core/library/tracking-utils';

import { newDefaultUnitFilters, newSortOptions } from '../../config/filters';
import { countAppliedFilters } from 'utils/search';
import { NewSortDropDown } from 'components/search/NewSortDropDown';

import './NewSearchPage.scss';
import { PAGE_TYPES } from '@sparefoot/tracking-events';
import { INTERNAL_PAGE_TYPES } from '../../config/constants';
import { withExperiment } from '@sparefoot/react-core/decorators';
import { toTitleCase } from '../../utils/formatting';

// Scroll offset accounts for sticky toolbar height + padding to prevent content
// from being hidden under the fixed header when scrolling to results
const STICKY_TOOLBAR_SCROLL_OFFSET = 138;

export const enhance = compose(
	withViewportWidth,
	withCookies,
	connect(
		searchSelector,
		{
			getSearchResults,
			getSearchLandingPage,
			hideSearchFilters,
			showSearchFilters,
			updateSearchFilters,
		},
		mapDispatchToActions,
	),
	withErrorHandler,
	withSearchHandler,
	withSearchPageHandlers,
	branch(
		(props) => props.loading && !props.searchId,
		renderComponent(LoadingPage),
	),
	withPageViewTracking(PAGE_TYPES.SEARCH),
	withExperiment(
		'sf_15810_ssdc_sticky_click_to_call_revisit',
		// Only log the impression if mobile
		(props) =>
			props?.withinCallCenterHours &&
			props?.deviceType &&
			props.deviceType !== 'desktop',
	),
	withExperiment('sf_16978_ssdc_clp_redesign'),
	withStateHandlers(
		() => ({
			facilitySelected: null, // this is the facility id of the facility that is selected
			viewListMap: true,
			filtersShowing: false, // this is for the new SearchFiltersTray
			defaultFilters: newDefaultUnitFilters, // This is the object of the default structure of the filters
			pinSelected: false, // this is the state of the pin on the map
		}),
		{
			handleFacilitySelected: () => (facilityId) => ({
				facilitySelected: facilityId,
			}),
			handleFacilityDeselected: () => () => ({ facilitySelected: null }),
			handlePinSelected: () => () => ({ pinSelected: true }),
			handlePinDeselected: () => () => ({ pinSelected: false }),
			closeFilters: () => () => ({ filtersShowing: false }),
			toggleFilters:
				({ filtersShowing }) =>
				() => ({ filtersShowing: !filtersShowing }),
			handleViewListMap:
				({ viewListMap }) =>
				() => {
					scrollToElement('#search-page-results', {
						offset: -STICKY_TOOLBAR_SCROLL_OFFSET,
					});
					return {
						viewListMap: !viewListMap,
					};
				},
		},
	),
);

export function NewSearchPage(props) {
	const {
		errorMessage,
		facilities,
		baseUrl,
		facilitySelected,
		faqContent,
		faqShowing,
		handleFacilityDeselected,
		handleFacilitySelected,
		handlePinDeselected,
		handlePinSelected,
		listingsPerPage,
		loading,
		meta,
		page,
		pageType,
		phone,
		searchContent,
		searchLocation,
		searchParams,
		onSearch,
		pinSelected,
		searchType,
		showInformedSearch,
		showRecommendedUnit,
		totalListings,
		totalPages,
		trackingPixel,
		handleSearch,
		viewListMap,
		handleViewListMap,
		facetCounts,
		storageTypeUrls,
		filtersShowing,
		closeFilters,
		toggleFilters,
		sf16978Variation,
		withinCallCenterHours,
		staticMapUrl,
		cloudfrontUrl,
		useNewSearch,
		defaultFilters,
		stateUrl,
		cityUrl,
		viewportWidth,
		headline,
	} = props;

	// Compute applied filters count directly from search params
	const filtersCount = countAppliedFilters(searchParams);
	const {
		city,
		state: stateAbbreviation,
		subPageType,
		vehicle,
	} = searchParams;
	const titleCaseCity = city ? toTitleCase(city) : '';
	const storageTypeUrlsSafe =
		pageType == INTERNAL_PAGE_TYPES.VEHICLE_LANDING_PAGE
			? storageTypeUrls?.vehicle
			: storageTypeUrls?.storage;
	const landingPageUrl = `${baseUrl}${storageTypeUrlsSafe}${subPageType ? subPageType : ''}`;

	let amenityType =
		pageType === INTERNAL_PAGE_TYPES.CITY_LANDING_PAGE && city
			? ''
			: pageType === INTERNAL_PAGE_TYPES.AMENITY_LANDING_PAGE &&
				  subPageType
				? subPageType
				: pageType === INTERNAL_PAGE_TYPES.VEHICLE_LANDING_PAGE &&
					  vehicle
					? vehicle
					: '';

	const shouldRenderSchema =
		(pageType === 'cityLandingPage' ||
			pageType === 'amenityLandingPage' ||
			pageType === 'vehicleLandingPage') &&
		page == 1;

	// Scroll selected facility card into view (desktop variant only)
	const scrollToFacilityCard = (facilityId) => {
		if (!facilityId) return;
		requestAnimationFrame(() => {
			scrollToElement(`#facility-card-${facilityId}`, { offset: -100 });
		});
	};

	return (
		<Page
			className="search-page sf-16978-ssdc-clp-redesign"
			trackingPixel={trackingPixel}
			loading={loading}
			pageType={pageType}
			title={meta.title}
			description={meta.description}
			canonical={meta.canonical}
			noIndex={meta.noindex}
			noFollow={meta.nofollow}
			prev={meta.prev}
			next={meta.next}
		>
			{(pageType === INTERNAL_PAGE_TYPES.CITY_LANDING_PAGE ||
				pageType === INTERNAL_PAGE_TYPES.AMENITY_LANDING_PAGE ||
				pageType === INTERNAL_PAGE_TYPES.VEHICLE_LANDING_PAGE) && (
				<Helmet>
					<script type="application/ld+json">
						{JSON.stringify({
							'@context': 'https://schema.org',
							'@type': 'WebSite',
							name: 'SelfStorage.com',
							url: 'https://www.selfstorage.com',
							potentialAction: {
								'@type': 'SearchAction',
								target: {
									'@type': 'EntryPoint',
									urlTemplate: `https://www.selfstorage.com/self-storage/${stateAbbreviation}/${city}`,
									inLanguage: 'en-US',
									actionPlatform: [
										'http://schema.org/DesktopWebPlatform',
										'http://schema.org/MobileWebPlatform',
									],
								},
								'query-input': {
									'@type': 'PropertyValueSpecification',
									valueRequired: 'http://schema.org/False',
									valueName: 'location',
								},
							},
						})}
					</script>
					<script type="application/ld+json">
						{JSON.stringify({
							'@context': 'https://schema.org',
							'@type': 'WebPage',
							name: `${titleCaseCity} Self Storage`,
							url: landingPageUrl,
							description: meta.description,
							mainEntity: {
								'@type': 'Place',
								name: `${titleCaseCity}, ${searchLocation?.state}`,
								hasMap: {
									'@type': 'Map',
									name: `${titleCaseCity} Self Storage Map`,
									url: staticMapUrl,
								},
							},
						})}
					</script>
				</Helmet>
			)}

			<NewSearchToolbar
				withinCallCenterHours={withinCallCenterHours}
				onSearch={handleSearch}
				location={searchLocation}
				onOpenFilters={toggleFilters}
				filtersCount={filtersCount}
				headline={headline}
				sticky
				// Filter tray props - desktop dropdown rendered inside toolbar for proper positioning
				filtersShowing={filtersShowing}
				closeFilters={closeFilters}
				searchParams={searchParams}
				onFilter={onSearch}
				searchType={searchType}
				unitFilters={defaultFilters}
				facetCounts={facetCounts}
				useNewSearch={useNewSearch}
			/>

			{/* Mobile filter tray rendered outside sticky toolbar to avoid
			    stacking context trap
			    Only rendered on mobile viewports — desktop uses the dropdown
			    inside NewSearchToolbar instead. 
				Prevents SSR issues with filtersSHowing defaulting to false, only true on user interaction
				Matches existing pattern on SFDC & in this codebase
				*/}
			{!isLarge(viewportWidth) && filtersShowing && (
				<SearchFiltersTray
					filtersShowing={filtersShowing}
					closeFilters={closeFilters}
					searchParams={searchParams}
					onFilter={onSearch}
					searchType={searchType}
					unitFilters={defaultFilters}
					facetCounts={facetCounts}
					useNewSearch={useNewSearch}
					className="mobile-filter-tray"
					idPrefix="mobile-"
				/>
			)}

			<SearchPageFooter
				withinCallCenterHours={withinCallCenterHours}
				viewListMap={viewListMap}
				handleViewListMap={handleViewListMap}
				onToggleListMap={handleViewListMap}
				onOpenFilters={toggleFilters}
				filtersCount={filtersCount}
				sticky
			/>

			<LazyHydrate
				id="lazy-search-page"
				noLazy={!showInformedSearch}
			>
				<div className="search-results-container">
					<SearchMap
						className={classnames({
							'display-view-list-map': viewListMap,
						})}
						facilities={facilities}
						baseUrl={baseUrl}
						facilitySelected={facilitySelected}
						handleFacilityDeselected={handleFacilityDeselected}
						handleFacilitySelected={handleFacilitySelected}
						handlePinDeselected={handlePinDeselected}
						handlePinSelected={handlePinSelected}
						handleSearch={handleSearch}
						location={searchLocation}
						mapShowing
						pinSelected={pinSelected}
						searchType={searchType}
						searchParams={searchParams}
						errorMessage={errorMessage}
						staticMapUrl={staticMapUrl}
						cloudfrontUrl={cloudfrontUrl}
						isNewSearchVariant={sf16978Variation === 'variation'}
						onScrollToFacilityCard={scrollToFacilityCard}
					/>
					<div
						id="search-page-results"
						className={classnames('results-container', {
							'display-view-list-map': !viewListMap,
						})}
					>
						<div className="results-n-pagination">
							{searchLocation && (
								<div className="results-header">
									<H2
										size="smallMedium"
										weight="standard"
										color="darkGray"
										className="title-results-pagination"
										last
									>
										<PaginationSummary
											listingsPerPage={listingsPerPage}
											pageListingsCount={
												facilities.length
											}
											page={page}
											totalItems={totalListings}
											amenityType={amenityType}
											city={searchLocation.city}
											state={searchLocation.state}
										/>
									</H2>
									<NewSortDropDown
										onChange={onSearch}
										value={searchParams?.sort}
										options={newSortOptions}
									/>
								</div>
							)}
							{/* {searchLocation.city === 'Los Angeles' && (
                                <AlertBanner
                                    theme="warning"
                                    icon={<FireIcon />}
                                    title="Important Alert"
                                    message="Due to the fires in Los Angeles, CA area, some facilities may not have units available at this time. Please call (844) 611-1659 for assistance."
                                />
                            )} */}
							<SearchResults
								error={errorMessage}
								facilities={facilities}
								baseUrl={baseUrl}
								facilitySelected={facilitySelected}
								handleFacilityDeselected={
									handleFacilityDeselected
								}
								handleFacilitySelected={handleFacilitySelected}
								handlePinDeselected={handlePinDeselected}
								handlePinSelected={handlePinSelected}
								isCLP={shouldRenderSchema}
								loading={loading}
								location={searchLocation}
								phone={phone}
								searchType={searchType}
								showRecommendedUnit={showRecommendedUnit}
								searchParams={searchParams}
								isNewSearchPage={
									sf16978Variation === 'variation'
								}
								cloudfrontUrl={cloudfrontUrl}
							/>
							<SearchPagination
								numPages={totalPages}
								searchParams={searchParams}
								selectedPage={page}
							/>
						</div>
						{searchContent &&
							searchLocation &&
							(pageType ===
								INTERNAL_PAGE_TYPES.CITY_LANDING_PAGE ||
								pageType ===
									INTERNAL_PAGE_TYPES.AMENITY_LANDING_PAGE ||
								pageType ===
									INTERNAL_PAGE_TYPES.VEHICLE_LANDING_PAGE) && (
								<SearchContent
									pageType={pageType}
									location={searchLocation}
									content={searchContent}
									searchParams={searchParams}
									faqContent={faqContent}
									faqShowing={faqShowing}
									amenityType={amenityType}
									cityUrl={cityUrl}
									stateUrl={stateUrl}
									baseUrl={baseUrl}
									searchType={searchType}
									averageMonthlyUnitPrice={get(
										searchContent,
										'averageMonthlyUnitPrice',
									)}
								/>
							)}
					</div>
				</div>
			</LazyHydrate>
		</Page>
	);
}

NewSearchPage.propTypes = {
	errorMessage: PropTypes.string,
	facilities: PropTypes.arrayOf(PropTypes.object),
	baseUrl: PropTypes.string,
	facilitySelected: PropTypes.number,
	faqContent: PropTypes.arrayOf(PropTypes.object),
	faqShowing: PropTypes.bool,
	handleFacilityDeselected: PropTypes.func,
	handleFacilitySelected: PropTypes.func,
	handlePinDeselected: PropTypes.func,
	handlePinSelected: PropTypes.func,
	listingsPerPage: PropTypes.number,
	loading: PropTypes.bool,
	meta: PropTypes.shape(metaPropTypes),
	onSearch: PropTypes.func,
	page: PropTypes.number,
	pageType: PropTypes.string,
	phone: phonePropType,
	pinSelected: PropTypes.bool,
	searchContent: PropTypes.object,
	searchLocation: PropTypes.object,
	searchParams: PropTypes.object,
	searchType: PropTypes.string,
	showInformedSearch: PropTypes.bool,
	showRecommendedUnit: PropTypes.bool,
	totalListings: PropTypes.number,
	totalPages: PropTypes.number,
	trackingPixel: PropTypes.string.isRequired,
	handleSearch: PropTypes.func.isRequired,
	viewListMap: PropTypes.bool,
	handleViewListMap: PropTypes.func,
	facetCounts: PropTypes.object,
	storageTypeUrls: PropTypes.shape({
		storage: PropTypes.string,
		vehicle: PropTypes.string,
	}),
	stateUrl: PropTypes.string,
	cityUrl: PropTypes.string,
	visitId: PropTypes.string,
	searchId: PropTypes.string,
	stickyTrayPhoneNumber: PropTypes.object,
	sf15810Variation: PropTypes.string,
	sf16978Variation: PropTypes.string,
	withinCallCenterHours: PropTypes.bool,
	staticMapUrl: PropTypes.string,
	cloudfrontUrl: PropTypes.string,
	useNewSearch: PropTypes.bool,
	defaultFilters: PropTypes.object,
	filtersShowing: PropTypes.bool,
	toggleFilters: PropTypes.func,
	closeFilters: PropTypes.func,
	viewportWidth: PropTypes.number,
	headline: PropTypes.string,
};

export default enhance(NewSearchPage);
